import { inject, Injectable } from '@angular/core';
import { ApiService } from '@app/gateway';
import { PaginatedProdutosResponse, ProdutosModel } from '../models';
import { StoreService } from './store.service';

@Injectable({ providedIn: 'root' })
export class ProdutoService {
  private readonly accountService = inject(StoreService);
  private readonly apiService = inject(ApiService);
  private readonly PAGE_SIZE = 52;

  /**
   * Busca produtos com paginação correta
   * @param page Número da página (começando em 1)
   * @param descricao Filtro de descrição (opcional)
   * @param grupo Filtro de grupo/categoria (opcional)
   * @returns Resposta paginada com informações de navegação
   */
  public async produtos(
    page: number = 1,
    descricao?: string,
    grupo?: string
  ): Promise<PaginatedProdutosResponse> {
    try {
      descricao = descricao?.trim().toLowerCase() ?? '';
      grupo = grupo?.trim().toUpperCase() ?? '';
      const storeName = this.accountService.getStoreName();

      const path = `sync/produtos/${storeName}/${page}?descricao=${descricao}&grupo=${grupo}&pageSize=${this.PAGE_SIZE}`;
      const response: any = await this.apiService.get<any>({ path });

      // Se o backend retorna um array simples, converter para formato paginado
      const paginatedResponse = this.normalizePaginationResponse(response, page);

      return paginatedResponse;
    } catch (e: any) {
      throw new Error(e.message);
    }
  }

  /**
   * Normaliza a resposta para garantir formato consistente
   */
  private normalizePaginationResponse(
    response: any,
    page: number
  ): PaginatedProdutosResponse {
    // Se backend já retorna o formato correto ou algo próximo disso
    if (
      response &&
      typeof response === 'object' &&
      'totalPages' in response
    ) {
      const currentPage = Number(response.currentPage ?? page) || page;
      const totalPages = Number(response.totalPages) || 1;
      const pageSize = Number(response.pageSize ?? this.PAGE_SIZE) || this.PAGE_SIZE;
      const produtos = Array.isArray(response.produtos) ? response.produtos : response?.produtos || [];
      const totalItems =
        typeof response.totalItems === 'number'
          ? response.totalItems
          : produtos.length + (currentPage - 1) * pageSize;
      const hasNextPage =
        typeof response.hasNextPage === 'boolean'
          ? response.hasNextPage
          : currentPage < totalPages;

      return {
        produtos: produtos.slice(0, pageSize),
        totalItems,
        currentPage,
        pageSize,
        totalPages,
        hasNextPage,
      };
    }

    // Fallback: se backend retorna apenas array
    const produtos = Array.isArray(response) ? response : response?.produtos || [];
    const pageSize = this.PAGE_SIZE;
    const hasExtraPage = produtos.length === pageSize;
    const totalItems = hasExtraPage
      ? page * pageSize + 1
      : (page - 1) * pageSize + produtos.length;

    return {
      produtos: produtos.slice(0, pageSize),
      totalItems,
      currentPage: page,
      pageSize,
      totalPages: hasExtraPage ? page + 1 : Math.ceil(totalItems / pageSize),
      hasNextPage: hasExtraPage,
    };
  }

  public async produto(id: string): Promise<ProdutosModel> {
    try {
      const storeName = this.accountService.getStoreName();
      const path: string = `sync/produtos/${storeName}/${id}/produto`;
      const produto: ProdutosModel = await this.apiService.get<ProdutosModel>({ path });
      return produto;
    } catch (e: any) {
      throw new Error(e.message);
    }
  }
}
